/**
 * Utility functions for tracking analytics events with Umami
 */

import { timeStamp } from "console";


// Define geolocation data type
interface GeolocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

/**
 * Get current URL data for tracking
 * 
 * @param includeQueryParams Whether to include query parameters in the URL
 * @returns Object with URL information
 */
export const getUrlData = (includeQueryParams = false) => {
  const path = window.location.pathname;
  const query = includeQueryParams ? window.location.search : '';
  const hash = window.location.hash;
  
  return {
    url: window.location.href,
    referrer: document.referrer || 'direct',
    title: document.title,
    path: `${path}${query}${hash}`
  };
};

/**
 * Get browser geolocation data if available and permitted
 * 
 * @returns Promise that resolves to geolocation data or null
 */
export const getBrowserGeolocation = (): Promise<GeolocationData | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    // Set a timeout to ensure we don't wait too long for geolocation
    const timeoutId = setTimeout(() => {
      console.log('Geolocation request timed out');
      resolve(null);
    }, 3000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        console.log('Geolocation error:', error.message);
        resolve(null);
      },
      { 
        enableHighAccuracy: false, 
        timeout: 2500,
        maximumAge: 60000 // Cache for 1 minute
      }
    );
  });
};

/**
 * Track a custom event with Umami
 * 
 * @param eventName The name of the event to track
 * @param eventData Optional data to associate with the event
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the event was tracked
 */
export const trackEvent = async (
  eventName: string, 
  eventData?: any, 
  includeGeolocation = false
): Promise<boolean> => {
  // Safely check if umami is available in the window object
  const umami = typeof window !== 'undefined' && window.umami;
  
  // If umami is not available, return false without throwing an error
  if (!umami) {
    return false;
  }

  try {
    // Add URL data to event data
    const urlData = getUrlData();
    let enrichedData: any = {
      ...urlData,
      eventData: JSON.stringify({eventData, url: urlData, timeStamp: new Date().toISOString()}),
      client_ip: '{{client_ip}}', // Umami will replace this with the actual IP
      timestamp: new Date().toISOString()
    };
    
    // Add browser geolocation if requested and available
    if (includeGeolocation) {
      const geolocation = await getBrowserGeolocation();
      if (geolocation) {
        enrichedData.browser_geolocation = geolocation;
      }
    }
    
    // Support both old and new API
    if (typeof umami.trackEvent === 'function') {
      umami.trackEvent(eventName, enrichedData);
    } else if (typeof umami.track === 'function') {
      umami.track(eventName, enrichedData);
    }

    // Track with Google Analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', eventName, {...urlData, timestamp: new Date().toISOString()});
    }
  
    return true;
  } catch (error) {
    console.error('Error tracking event:', error);
    return false;
  }
};

/**
 * Track a page view with Umami
 * 
 * @param url Optional URL to track instead of current page
 * @param includeQueryParams Whether to include query parameters in the URL
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the page view was tracked
 */
export const trackPageView = async (
  url?: string, 
  includeQueryParams = false,
  includeGeolocation = false
): Promise<boolean> => {
  // Safely check if umami is available in the window object
  const umami = typeof window !== 'undefined' && window.umami;
  
  // If umami is not available, return false without throwing an error
  if (!umami) {
    return false;
  }

  try {
    // Determine the URL to track
    let urlToTrack = url;
    
    if (!urlToTrack) {
      const path = window.location.pathname;
      const query = includeQueryParams ? window.location.search : '';
      const hash = window.location.hash;
      urlToTrack = `${path}${query}${hash}`;
    }
    
    // Get additional URL data
    const urlData = getUrlData(includeQueryParams);
    let enrichedData: any = {
      ...urlData,
      client_ip: '{{client_ip}}', // Umami will replace this with the actual IP
      timestamp: new Date().toISOString()
    };
    
    // Add browser geolocation if requested and available
    if (includeGeolocation) {
      const geolocation = await getBrowserGeolocation();
      if (geolocation) {
        enrichedData.browser_geolocation = geolocation;
      }
    }
    
    // Track the page view
    if (typeof umami.trackView === 'function') {
      umami.trackView(urlToTrack);
      // Also track as an event to include the enriched data
      if (typeof umami.trackEvent === 'function') {
        umami.trackEvent('pageview', enrichedData);
      }
    } else if (typeof umami.track === 'function') {
      umami.track(undefined, enrichedData);
    }
    return true;
  } catch (error) {
    console.error('Error tracking page view:', error);
    return false;
  }
};

/**
 * Track a button click
 * 
 * @param buttonName Name of the button that was clicked
 * @param location Optional location where the button was clicked
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the event was tracked
 */
export const trackButtonClick = async (
  buttonName: string, 
  location?: string,
  includeGeolocation = false
): Promise<boolean> => {
  return trackEvent('button_click', { 
    button_name: buttonName,
    location: location || window.location.pathname
  }, includeGeolocation);
};

/**
 * Track a form submission
 * 
 * @param formName Name of the form that was submitted
 * @param data Additional data about the form submission
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the event was tracked
 */
export const trackFormSubmission = async (
  formName: string, 
  data: any = {},
  includeGeolocation = false
): Promise<boolean> => {
  return trackEvent('form_submission', { 
    form_name: formName,
    ...data
  }, includeGeolocation);
};

/**
 * Track a search query
 * 
 * @param searchTerm The search term that was entered
 * @param resultsCount Optional count of search results
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the event was tracked
 */
export const trackSearch = async (
  searchTerm: string, 
  resultsCount?: number,
  includeGeolocation = false
): Promise<boolean> => {
  return trackEvent('search', { 
    search_term: searchTerm,
    results_count: resultsCount
  }, includeGeolocation);
};

/**
 * Track a subscription action
 * 
 * @param planName Name of the subscription plan
 * @param planPrice Price of the subscription plan
 * @param includeGeolocation Whether to include browser geolocation data if available
 * @returns Promise that resolves to boolean indicating if the event was tracked
 */
export const trackSubscription = async (
  planName: string, 
  planPrice: number,
  includeGeolocation = false
): Promise<boolean> => {
  return trackEvent('subscription', { 
    plan_name: planName,
    plan_price: planPrice
  }, includeGeolocation);
};

/**
 * Track latest blog specific events for SEO analysis
 * 
 * @param action The action being tracked (view, share, subscribe, etc.)
 * @param blogData Additional blog context data
 */
export const trackLatestBlogEvent = (action: string, blogData?: any) => {
  try {
    const eventData = {
      event_category: 'Latest Blog',
      event_label: action,
      custom_parameters: {
        blog_id: blogData?.obg_id || 'unknown',
        blog_title: blogData?.title || 'Latest KPLC Outages',
        is_latest: true,
        user_type: blogData?.isAuthenticated ? 'authenticated' : 'anonymous',
        ...getUrlData(true)
      }
    };

    // Track with Umami
    trackEvent('latest_blog_event', eventData);

  } catch (error) {
    console.error('Error tracking latest blog event:', error);
  }
};

/**
 * Track blog sharing events with enhanced context
 * 
 * @param method The sharing method (native, copy, social)
 * @param blogData Blog context data
 */
export const trackBlogShare = (method: string, blogData?: any) => {
  trackLatestBlogEvent('share', {
    ...blogData,
    share_method: method,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track subscription events from latest blog
 * 
 * @param action The subscription action (signup, login, manage)
 * @param blogData Blog context data
 */
export const trackBlogSubscription = (action: string, blogData?: any) => {
  trackLatestBlogEvent('subscription', {
    ...blogData,
    subscription_action: action,
    timestamp: new Date().toISOString()
  });
}; 